import React, { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import { useLocation } from "react-router-dom";
import { Menu, X, CalendarClock } from "lucide-react";
import Logo from "./Logo";
import { useSectionNav } from "../../lib/useSectionNav";

const NAV = [
  { label: "Tjenester", href: "#tjenester" },
  { label: "Priser", href: "#priser" },
  { label: "Butikk", href: "/butikk" },
  { label: "Selg enheten din", href: "#selg" },
  { label: "Om oss", href: "#om-oss" },
  { label: "Kontakt", href: "#kontakt" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const sectionNav = useSectionNav();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    flushSync(() => setOpen(false));
    sectionNav(href);
  };
  const isActive = (href) => !href.startsWith("#") && pathname.startsWith(href);
  const navHref = (href) => (href.startsWith("#") ? (pathname === "/" ? href : `/${href}`) : href);

  return (
    <header
      className={`sticky top-0 z-50 bg-navy transition-shadow ${scrolled ? "shadow-lg shadow-navy/20" : ""}`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <a href="/" onClick={(e) => { e.preventDefault(); go("#hjem"); }} className="shrink-0">
            <Logo />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={navHref(n.href)}
                onClick={(e) => { e.preventDefault(); go(n.href); }}
                className={`text-sm font-medium transition-colors hover:text-electric ${isActive(n.href) ? "text-warm" : "text-slate-200"}`}
                data-testid={`nav-${n.href.slice(1)}`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => go("#bestill")}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-warm px-5 py-2.5 text-sm font-semibold text-navy hover:bg-warm-dark active:scale-[0.98] transition-all"
              data-testid="header-bestill-time-button"
            >
              <CalendarClock className="w-4 h-4" />
              Bestill time
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-white hover:bg-white/10"
              aria-label="Meny"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-navy px-4 pb-5 pt-2" data-testid="mobile-menu">
          <nav className="flex flex-col">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={navHref(n.href)}
                onClick={(e) => { e.preventDefault(); go(n.href); }}
                className={`py-3 text-base font-medium border-b border-white/10 hover:text-electric ${isActive(n.href) ? "text-warm" : "text-slate-100"}`}
                data-testid={`mobile-nav-${n.href.slice(1)}`}
              >
                {n.label}
              </a>
            ))}
            <button
              onClick={() => go("#bestill")}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-warm px-5 py-3 text-base font-semibold text-navy"
              data-testid="mobile-bestill-time-button"
            >
              <CalendarClock className="w-5 h-5" />
              Bestill time
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
